import './styles.css';
import { Game } from './game/Game';
import { HeaderScene, type BrickAvatar } from './systems/HeaderScene';

// Web build: no Tauri filesystem access, so the browse / import bits get
// hidden and a plain "Load sample" file picker takes their place. Tag the
// body so CSS can drive the visibility split without JS re-checks.
// Tauri v1 injects `window.__TAURI__`; v2 uses `window.__TAURI_INTERNALS__`
// and exposes `window.isTauri = true` — check all three.
const w = window as unknown as {
  __TAURI__?: unknown;
  __TAURI_INTERNALS__?: unknown;
  isTauri?: boolean;
};
const isTauri = !!(w.__TAURI__ || w.__TAURI_INTERNALS__ || w.isTauri);
document.body.classList.add(isTauri ? 'is-tauri' : 'is-web');

const isMobileLayout = window.matchMedia('(max-width: 640px), (max-aspect-ratio: 3/4)').matches;

// Publish the mobile layout parameters as CSS custom properties. Every mobile
// element sizes itself as N × var(--cell), where --cell is the largest square
// that satisfies both `viewport_width / cols` and `(viewport_height - hud) /
// total_rows`. Tweak a row count here and every dependent size (visualizer,
// canvas, sequencer) rescales together — no more magic vh / aspect-ratio
// constants. Keep in sync with Game.ts's GRID_COLS / FIELD_ROWS.
if (isMobileLayout) {
  const cols = 16;
  const visRows = 8;
  const fieldRows = 15;
  const seqRows = 4;
  const hudH = 44;
  // Dark strip between canvas bottom and sequencer — gives the player a
  // place to rest their thumb without covering the paddle. Consumed as
  // padding-bottom on #game-stack; input handler tracks clientX
  // window-wide so dragging in this zone still steers the paddle.
  const thumbRows = 3;
  const root = document.documentElement.style;
  root.setProperty('--cols', String(cols));
  root.setProperty('--vis-rows', String(visRows));
  root.setProperty('--field-rows', String(fieldRows));
  root.setProperty('--seq-rows', String(seqRows));
  root.setProperty('--thumb-rows', String(thumbRows));
  root.setProperty('--hud-h', `${hudH}px`);
}

const canvas = document.querySelector<HTMLCanvasElement>('#game-canvas');
if (!canvas) {
  throw new Error('Missing #game-canvas element.');
}
const game = new Game(canvas);
game.start();

// First-run hint arrows above the paddle. Fades out on the first pointer
// interaction inside the game area — after that the player clearly knows
// to drag. Also removed once the ball leaves the paddle (any subsequent
// launch means they already figured it out). Mobile-only via CSS.
const paddleHint = document.querySelector<HTMLElement>('#paddle-hint');
if (paddleHint) {
  const dismissHint = () => {
    if (paddleHint.dataset.dismiss === '1') return;
    paddleHint.dataset.dismiss = '1';
    window.setTimeout(() => paddleHint.remove(), 260);
  };
  const stack = document.querySelector<HTMLElement>('#game-stack');
  stack?.addEventListener('pointerdown', dismissHint, { once: true });
  // Fallback: auto-dismiss after 12s so it never lingers forever.
  window.setTimeout(dismissHint, 12000);
}

// Drop the black boot cover once we've had at least one frame with the app
// laid out — kills the flash of unstyled panels on first paint without
// showing an obviously-empty game canvas.
requestAnimationFrame(() => {
  const cover = document.querySelector<HTMLElement>('#boot-cover');
  if (!cover) return;
  cover.setAttribute('data-hide', '1');
  window.setTimeout(() => cover.remove(), 240);
});

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    game.dispose();
  });
}

// Full-screen splash — shown on every surface as an intro / audio-unlock
// gesture. Tapping START closes the splash and reveals the "Ready to play?"
// overlay inside the game area, where the player can tinker with the sound
// / sequencer panels before hitting Ready! to actually launch.
const splash = document.querySelector<HTMLElement>('#mobile-splash');
if (splash) {
  splash.setAttribute('data-open', '1');

  // Randomize NOIZ letter colors on an interval — each letter picks an
  // independent color from the game palette so the flashing feels chaotic
  // rather than a rotating cycle.
  const noizLetters = Array.from(
    splash.querySelectorAll<HTMLElement>('.splash-title .title-n, .splash-title .title-o, .splash-title .title-i, .splash-title .title-z'),
  );
  const noizPalette = ['#ff1e5a', '#ff6a1a', '#ffd60a', '#26e884', '#3d5cff', '#b64bff'];
  const noizTick = () => {
    // Sample without replacement so no two letters share a color on the same
    // tick. Palette has 6 entries and there are only 4 NOIZ letters, so a
    // simple partial Fisher-Yates over a copy is enough.
    const pool = noizPalette.slice();
    for (let i = 0; i < noizLetters.length; i++) {
      const idx = Math.floor(Math.random() * pool.length);
      noizLetters[i]!.style.color = pool[idx]!;
      pool.splice(idx, 1);
    }
  };
  noizTick();
  const noizInterval = window.setInterval(noizTick, 220);

  // Reuse the in-game HeaderScene as an animated background: seed with a
  // random palette of floating bricks and periodically explode one so the
  // splash reads as an interactive teaser of the actual game. Torn down
  // when the user taps Start to release the WebGL context.
  const splashCanvas = document.querySelector<HTMLCanvasElement>('#splash-canvas');
  let splashScene: HeaderScene | null = null;
  let splashRaf = 0;
  let explodeTimer = 0;
  let pulseTimer = 0;
  const paletteHex = ['#ff1e5a', '#ff6a1a', '#ffd60a', '#26e884', '#3d5cff', '#b64bff'];
  const seedAvatars = (): BrickAvatar[] => {
    const out: BrickAvatar[] = [];
    for (let i = 0; i < 22; i++) {
      out.push({
        id: i,
        color: paletteHex[i % paletteHex.length]!,
        sizeUnits: 1 + Math.floor(Math.random() * 3),
        row: i % 6,
        step: i % 8,
      });
    }
    return out;
  };
  if (splashCanvas) {
    splashScene = new HeaderScene(splashCanvas, { showTitle: false });
    let avatars = seedAvatars();
    splashScene.syncBricks(avatars);
    let prev = performance.now();
    const tick = (now: number) => {
      const delta = Math.min(0.05, (now - prev) / 1000);
      prev = now;
      splashScene!.update(delta);

      // Trigger a shatter every ~1.6s so fragments keep populating the scene.
      explodeTimer += delta;
      if (explodeTimer > 1.6 && avatars.length > 4) {
        explodeTimer = 0;
        const victim = avatars[Math.floor(Math.random() * avatars.length)]!;
        splashScene!.explodeBrick(victim.id);
        avatars = avatars.filter((a) => a.id !== victim.id);
        // Reseed when depleted so the loop never runs dry.
        if (avatars.length <= 4) {
          avatars = seedAvatars();
          splashScene!.syncBricks(avatars);
        }
      }

      // White twinkle undulation — pulse a random matching row/step every ~0.4s.
      pulseTimer += delta;
      if (pulseTimer > 0.35) {
        pulseTimer = 0;
        splashScene!.pulseNote(
          Math.floor(Math.random() * 6),
          Math.floor(Math.random() * 8),
        );
      }
      splashRaf = requestAnimationFrame(tick);
    };
    splashRaf = requestAnimationFrame(tick);
  }

  document.querySelector('#mobile-start')?.addEventListener('click', () => {
    splash.removeAttribute('data-open');
    if (splashRaf) cancelAnimationFrame(splashRaf);
    window.clearInterval(noizInterval);
    splashScene?.dispose();
    splashScene = null;
    // Don't auto-start on mobile any more — closing the splash reveals the
    // "Ready to play?" overlay (awaitingStart is now true on every surface),
    // and the Ready! button in that overlay kicks off the countdown. Gives
    // mobile players the same "explore first, launch when ready" flow as
    // desktop instead of dropping them into a countdown immediately.
  }, { once: true });
}
